import React, { useContext, useEffect, useState } from 'react';
import {
    OverlayConfigContext,
    OverlayVisibilityContext,
    usePaginationDisplayContext,
} from '../context';
import { Pages } from '../types';
import styles from './ReportEventRenderer/ReportEventRenderer.module.scss';

interface LazyLoadedLibs {
    ReactDOM: null | any;
    Modal: null | any;
}

interface ReportEventRendererProps {
    domNodeId?: string;
    showOverlay?: () => void;
    domNode?: Element;
}

const getMountingPoint = (
    domNodeId?: string,
    domNode?: Element,
): Element | boolean => {
    if (domNode) {
        return domNode;
    }
    if (domNodeId) {
        return document.getElementById(domNodeId) as Element;
    }

    return false;
};

interface RenderViaPortalProps {
    domNodeId?: string;
    domNode?: Element;
    children: React.ReactNode;
}

const RenderViaPortal: React.FC<RenderViaPortalProps> = (props) => {
    const { overlayConfig } = useContext(OverlayConfigContext);
    const { isVisible, setVisible } = useContext(OverlayVisibilityContext);
    const [mountPoint, setMountPoint] = useState({});
    const { page } = usePaginationDisplayContext();

    const [{ ReactDOM, Modal }, setAsyncLibs] = useState<
        Partial<LazyLoadedLibs>
    >({
        ReactDOM: null,
        Modal: null,
    });

    const overlayConfigWithVisibility = {
        ...overlayConfig,
        isShown: isVisible,
        dangerouslySetContainerClassName: {
            __className:
                page === Pages.form
                    ? styles.root__tall || ''
                    : styles.root || '',
        },
        onClose: () => setVisible && setVisible({ value: false }),
    };

    //Only import necessary libs once module is being rendered
    useEffect(() => {
        if (isVisible && !ReactDOM && !Modal) {
            const reactDomPromise = import('react-dom');
            const modalPromise = import('@eventbrite/legacy-eds.modal');
            Promise.all([reactDomPromise, modalPromise]).then((values) => {
                setAsyncLibs({
                    ReactDOM: values[0].default,
                    Modal: values[1].Modal,
                });
            });
        }
    }, [isVisible, ReactDOM, Modal]);

    useEffect(() => {
        setMountPoint(getMountingPoint(props.domNodeId, props.domNode));
    }, [props.domNodeId, props.domNode]);

    if (ReactDOM === null || Modal === null || !mountPoint) {
        return null;
    }

    return ReactDOM.createPortal(
        <div className="report-event-wrapper">
            <Modal {...overlayConfigWithVisibility}>{props.children}</Modal>
        </div>,
        mountPoint as Element,
    );
};

/**
 * Controls the rendering and hiding of the overlay
 * component used to render the contact organizer
 * application.
 *
 * Passes modal config through and allows updates to
 * be made by leaves.
 *
 * @param props
 */
export const ReportEventRenderer: React.FC<React.PropsWithChildren<ReportEventRendererProps>> = ({ children, domNodeId, domNode }) => {
    return (
        <RenderViaPortal domNodeId={domNodeId} domNode={domNode}>
            {children}
        </RenderViaPortal>
    );
};
